import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { ActionFlag, BuilderDialog } from '@makyoapp/makyo-common'
import { Dialog16, Edit16, Trash16 } from '@makyoapp/makyo-icon/icon'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, ButtonVariant } from '@makyoapp/makyo-ui'

import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useVariableTextSingleCell } from '@/hooks/__project__/useVariableTextSingleCell'

/**
 * DialogListItem is a function component that renders an item in a list of dialogs.
 * It provides options to edit, preview, and delete the dialog.
 *
 * @param {object} props - The props that define the component's behavior and state.
 * @param {BuilderDialog} props.dialog - The dialog object that contains the details of the dialog being displayed.
 *
 * @returns {JSX.Element} Returns a JSX element that renders a dialog list item with options to edit, preview, and delete the dialog.
 */
function DialogListItem({ dialog }: { dialog: BuilderDialog }) {
  const { t } = useTranslation()
  const { setDialogAction, deleteDialog } = useAppBuilderContext()

  const dialogName = useVariableTextSingleCell(dialog.name)

  return (
    <div className={clsx('flex items-center', 'hover:bg-zinc-200 dark:hover:bg-slate-700 rounded-md')}>
      <div
        onClick={() => {
          setDialogAction({
            flag: ActionFlag.Edit,
            dialog,
          })
        }}
        className={clsx('w-full px-2 h-10 txt flex-c')}
      >
        <MaterialIcon size={20} icon={dialog.icon ?? 'info'} />
        <div className={clsx('txt')}>{dialogName}</div>
      </div>
      <div className='shrink-0 flex items-center ltr:pr-2 rtl:pl-2'>
        <Button
          variant={ButtonVariant.Ghost}
          icon={<Edit16 />}
          noPadding
          squared
          title={t('Edit Dialog')}
          onClick={() =>
            setDialogAction({
              flag: ActionFlag.Edit,
              dialog,
            })
          }
          noTip
        />
        <Button
          variant={ButtonVariant.Ghost}
          icon={<Dialog16 width={16} height={16} />}
          noPadding
          squared
          title={t('Preview Dialog')}
          onClick={() =>
            setDialogAction({
              flag: ActionFlag.Preview,
              dialog,
            })
          }
          noTip
        />
        <Button
          variant={ButtonVariant.Ghost}
          icon={<Trash16 />}
          noPadding
          squared
          onClick={async () =>
            /*
             setDialogAction({
             flag: ActionFlag.Delete,
             dialog
             })
             */
            await deleteDialog(dialog.id)
          }
          title={t('Delete Dialog')}
          noTip
        />
      </div>
    </div>
  )
}

export default DialogListItem
